import React, { useMemo, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// Utility function to combine class names
const cn = (...classes) => {
  return classes.filter(Boolean).join(" ");
};

const TextEditor = ({
  label,
  title,
  value,
  data,
  onChange,
  onBlur,
  ErrorMessage,
  id,
  placeholder = "Enter content here...",
  disabled,
  readOnly,
  minHeight = 200,
  className,
  ...rest
}) => {
  const quillRef = useRef(null);

  // Support both the current prop names (value/label/disabled) and the
  // legacy ones (data/title/readOnly) still used by several callers
  // (settings, legal_information, landingPage). Without this the legacy
  // callers pass `data`, `value` stays undefined, and Quill is locked empty.
  const resolvedValue = value ?? data ?? "";
  const resolvedLabel = label ?? title;
  const isDisabled = disabled ?? readOnly ?? false;

  // Image upload handler
  const imageHandler = () => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      const formData = new FormData();
      formData.append("upload", file);

      try {
        const uploadUrl = import.meta.env.VITE_SERVER_API_BASEURL_IMAGE || "";
        const response = await fetch(`${uploadUrl}/upload`, {
          method: "POST",
          body: formData,
        });

        const result = await response.json();
        if (result.url) {
          const quill = quillRef.current?.getEditor();
          const range = quill?.getSelection();
          const index = range ? range.index : 0;
          quill?.insertEmbed(index, "image", result.url);
        }
      } catch (error) {
        console.error("Image upload failed:", error);
      }
    };
  };

  // Video embed handler
  const videoHandler = () => {
    const url = prompt("Enter video URL (YouTube, Vimeo, etc.):");
    if (!url) return;

    const quill = quillRef.current?.getEditor();
    const range = quill?.getSelection();
    const index = range ? range.index : 0;
    quill?.insertEmbed(index, "video", url);
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          [{ font: [] }],
          [{ size: [] }],
          ["bold", "italic", "underline", "strike", "blockquote"],
          [
            { list: "ordered" },
            { list: "bullet" },
            { indent: "-1" },
            { indent: "+1" },
          ],
          [{ color: [] }, { background: [] }],
          [{ align: [] }],
          ["link", "image", "video"],
          ["code-block"],
          ["clean"],
        ],
        handlers: {
          image: imageHandler,
          video: videoHandler,
        },
      },
      clipboard: {
        matchVisual: false,
      },
    }),
    []
  );

  const formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "link",
    "image",
    "video",
    "color",
    "background",
    "align",
    "code-block",
  ];

  return (
    <div
      className={cn(
        "relative flex flex-col items-start gap-2 w-full",
        ErrorMessage && "mb-6",
        className
      )}
    >
      {resolvedLabel && (
        <label htmlFor={id} className="text-sm font-medium text-foreground">
          {resolvedLabel}
        </label>
      )}

      <div
        className={cn(
          "w-full rounded-md border border-input bg-input",
          isDisabled && "cursor-not-allowed opacity-50"
        )}
        style={{ minHeight: `${minHeight}px` }}
      >
        <style>{`
          .quill {
            background: hsl(var(--background, 0 0% 100%));
            border-radius: 0.375rem;
          }

          .ql-container {
            min-height: ${minHeight - 40}px;
            color: hsl(var(--foreground, 0 0% 0%));
            background: hsl(var(--input, 0 0% 100%));
            border-color: hsl(var(--border, 0 0% 80%));
            font-family: inherit;
          }

          .ql-editor {
            min-height: ${minHeight - 40}px;
            color: hsl(var(--foreground, 0 0% 0%));
            padding: 12px 15px;
          }

          .ql-editor.ql-blank::before {
            color: hsl(var(--muted-foreground, 0 0% 50%));
            font-style: normal;
          }

          .ql-toolbar {
            background: hsl(var(--background, 0 0% 100%));
            border-color: hsl(var(--border, 0 0% 80%));
            border-top-left-radius: 0.375rem;
            border-top-right-radius: 0.375rem;
          }

          .ql-toolbar .ql-stroke {
            stroke: #444444 !important;
          }

          .ql-toolbar .ql-fill {
            fill: #444444 !important;
          }

          .ql-toolbar .ql-picker-label {
            color: #444444 !important;
          }

          .ql-toolbar button {
            color: #444444 !important;
          }

          .ql-toolbar button:hover,
          .ql-toolbar button.ql-active {
            background: #e0e0e0 !important;
            color: #000000 !important;
          }

          .ql-toolbar .ql-picker-options {
            background: #ffffff !important;
            border-color: #cccccc !important;
            color: #444444 !important;
          }

          .ql-toolbar .ql-picker-item:hover {
            background: #e0e0e0 !important;
            color: #000000 !important;
          }

          .ql-snow .ql-picker.ql-expanded .ql-picker-options {
            border-color: #cccccc !important;
          }

          .ql-snow .ql-stroke.ql-thin,
          .ql-snow .ql-thin {
            stroke: #444444 !important;
          }

          .ql-snow .ql-fill,
          .ql-snow .ql-stroke.ql-fill {
            fill: #444444 !important;
          }

          .ql-snow .ql-picker {
            color: #444444 !important;
          }

          .ql-snow .ql-picker-options {
            background: #ffffff !important;
            border-color: #cccccc !important;
          }

          .ql-snow .ql-picker-item {
            color: #444444 !important;
          }

          .ql-snow .ql-picker-item:hover,
          .ql-snow .ql-picker-item.ql-selected {
            background: #e0e0e0 !important;
            color: #000000 !important;
          }

          .ql-snow .ql-tooltip {
            background: hsl(var(--background, 0 0% 100%));
            background: #ffffff;
            border-color: hsl(var(--border, 0 0% 80%));
            border-color: #cccccc;
            color: hsl(var(--foreground, 0 0% 0%));
            color: #444444;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          }

          .ql-snow .ql-tooltip input {
            background: hsl(var(--input, 0 0% 100%));
            background: #ffffff;
            border-color: hsl(var(--border, 0 0% 80%));
            border-color: #cccccc;
            color: hsl(var(--foreground, 0 0% 0%));
            color: #444444;
          }

          .ql-snow .ql-tooltip a {
            color: hsl(var(--primary, 210 100% 50%));
            color: #1976d2;
          }

          .ql-snow .ql-tooltip a:hover {
            color: hsl(var(--primary, 210 100% 50%));
            color: #1976d2;
            opacity: 0.8;
          }

          .ql-editor img,
          .ql-editor video {
            max-width: 100%;
            height: auto;
          }

          .ql-editor blockquote {
            border-left: 4px solid hsl(var(--primary, 210 100% 50%));
            border-left: 4px solid #1976d2;
            padding-left: 16px;
            margin: 16px 0;
            color: hsl(var(--muted-foreground, 0 0% 50%));
            color: #666666;
          }

          .ql-editor code,
          .ql-editor pre {
            background: hsl(var(--muted, 0 0% 95%));
            background: #f5f5f5;
            border-radius: 4px;
            padding: 2px 6px;
            font-family: 'Courier New', monospace;
          }

          .ql-editor pre {
            padding: 12px;
            margin: 16px 0;
            overflow-x: auto;
          }

          .ql-editor a {
            color: hsl(var(--primary, 210 100% 50%));
            color: #1976d2;
            text-decoration: underline;
          }

          .ql-editor a:hover {
            opacity: 0.8;
          }
        `}</style>

        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={resolvedValue}
          onChange={onChange}
          onBlur={onBlur}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          readOnly={isDisabled}
          style={{
            minHeight: `${minHeight}px`,
          }}
          {...rest}
        />
      </div>

      {ErrorMessage && (
        <span className="absolute -bottom-6 left-0 text-sm text-destructive">
          {ErrorMessage}
        </span>
      )}
    </div>
  );
};

export default TextEditor;
